/**
 * DealIntelligenceDemo — second AI experience, mounted inside /forgeos.
 * "Which Strategy Wins?" — compares Fix & Flip / Rental / New Construction / Land
 * for the same address, recommends the best.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, Sparkles, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import ForgeVisionLoader from "@/components/forge-vision/ForgeVisionLoader";

const STRATEGY_COLORS = ["#5B7FB0", "#8AA3CC", "#DCEEFF", "#3D5A85"];

export default function DealIntelligenceDemo() {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [purchasePrice, setPurchasePrice] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const t = setTimeout(async () => {
            const q = query.trim();
            if (q.length < 3 || selected?.display === q) { setSuggestions([]); setSuggestOpen(false); return; }
            try {
                const { data } = await api.get(`/public/forge-vision/address-suggest?q=${encodeURIComponent(q)}`);
                setSuggestions(data.suggestions || []);
                setSuggestOpen((data.suggestions || []).length > 0);
            } catch {}
        }, 250);
        return () => clearTimeout(t);
    }, [query, selected]);

    const submit = async (e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) return;
        setLoading(true); setResult(null); setSuggestOpen(false);
        const [{ data }] = await Promise.all([
            (async () => {
                try {
                    const payload = { query: q };
                    if (purchasePrice) payload.purchase_price = Number(purchasePrice);
                    return await api.post("/public/deal-intelligence/analyze", payload);
                } catch (err) {
                    toast.error(err?.response?.data?.detail || "Analysis failed");
                    return { data: null };
                }
            })(),
            new Promise((r) => setTimeout(r, 4500)),
        ]);
        if (data) setResult(data);
        setLoading(false);
    };

    const shareUrl = result?.share_slug ? `${window.location.origin}/deal-intelligence/r/${result.share_slug}` : null;
    const copyShare = async () => {
        if (!shareUrl) return;
        try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); } catch {}
    };

    const recommended = result?.strategies?.find((s) => s.key === result.recommended_key);

    return (
        <section
            className="section relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0F2747 0%, #3D5A85 55%, #5B7FB0 100%)" }}
            data-testid="deal-intelligence-section"
        >
            <div className="container-editorial relative">
                <div className="max-w-3xl">
                    <div className="chip chip-ai"><Sparkles size={10} /> Forge Deal Intelligence · Live Demo</div>
                    <h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.95] italic">Which Strategy Wins?</h2>
                    <p className="mt-6 text-lg md:text-xl text-arch-white/80 leading-relaxed">
                        Enter any property. ForgeOS instantly evaluates every possible strategy and tells you which one earns the most.
                    </p>
                </div>

                <form onSubmit={submit} className="mt-10 max-w-3xl relative" data-testid="di-form">
                    <div className="glass p-2 flex flex-col md:flex-row items-stretch md:items-center gap-2">
                        <input
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                            placeholder="Property address or parcel ID"
                            className="flex-1 bg-transparent text-arch-white placeholder:text-arch-white/40 px-4 py-3 text-sm focus:outline-none"
                            data-testid="di-input"
                        />
                        <input
                            value={purchasePrice}
                            onChange={(e) => setPurchasePrice(e.target.value.replace(/[^0-9]/g, ""))}
                            placeholder="Purchase price (optional)"
                            className="w-full md:w-52 bg-transparent border-t md:border-t-0 md:border-l border-arch-white/10 text-arch-white placeholder:text-arch-white/40 px-4 py-3 text-sm focus:outline-none"
                            data-testid="di-price"
                        />
                        <button type="submit" disabled={loading} className="bg-arch-white text-obsidian px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-ice transition-colors disabled:opacity-50" data-testid="di-submit">
                            {loading ? "Analyzing…" : "Analyze Strategies"}
                        </button>
                    </div>
                    {suggestOpen && suggestions.length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1 border border-steel/50 bg-obsidian/95 backdrop-blur z-20 max-h-72 overflow-y-auto">
                            {suggestions.map((s, i) => (
                                <button key={i} type="button" onClick={() => { setSelected(s); setQuery(s.display); setSuggestOpen(false); }} className="w-full text-left px-4 py-3 border-b border-arch-white/5 last:border-b-0 text-sm text-arch-white/80 hover:bg-arch-white/5" data-testid={`di-sugg-${i}`}>
                                    <div className="font-display">{s.address_line || s.display}</div>
                                    <div className="text-[11px] text-arch-white/50">{[s.city, s.state].filter(Boolean).join(" · ")}</div>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="mt-2 text-[10px] text-arch-white/50">Data © OpenStreetMap contributors · Demonstration Analysis — not investment advice</div>
                </form>

                {loading && <div className="mt-10 max-w-3xl"><ForgeVisionLoader /></div>}

                {result && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-12" data-testid="di-results">
                        <div className="flex items-center flex-wrap gap-2 mb-4">
                            <div className="chip chip-ai"><Sparkles size={10}/> Demonstration Analysis</div>
                            {shareUrl && (
                                <button onClick={copyShare} className="ml-auto inline-flex items-center gap-2 border border-ice/40 text-ice px-3 py-1.5 text-xs hover:bg-ice/10" data-testid="di-share">
                                    {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Share</>}
                                </button>
                            )}
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {result.strategies.map((s, i) => {
                                const isRec = s.key === result.recommended_key;
                                return (
                                    <div key={s.key} className={`p-6 border relative ${isRec ? "bg-arch-white text-obsidian border-arch-white shadow-[0_0_40px_rgba(220,238,255,0.25)]" : "bg-obsidian/60 border-arch-white/15"}`} data-testid={`di-card-${s.key}`}>
                                        {isRec && <div className="absolute -top-3 left-4 bg-navy text-arch-white text-[9px] uppercase tracking-[0.2em] px-2 py-1">Recommended</div>}
                                        <div className={`text-[10px] uppercase tracking-widest ${isRec ? "text-navy" : "text-steel-light"}`}>Strategy {String(i+1).padStart(2,"0")}</div>
                                        <div className={`mt-3 font-display italic text-2xl ${isRec ? "text-navy" : ""}`}>{s.label}</div>
                                        <div className="mt-4 space-y-2">
                                            {s.kpis.map((k) => (
                                                <div key={k.label} className="flex justify-between text-sm">
                                                    <span className={isRec ? "text-obsidian/60" : "text-arch-white/60"}>{k.label}</span>
                                                    <span className={`font-medium ${isRec ? "text-navy" : "text-arch-white"}`}>{k.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-4 h-24">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={s.chart}>
                                                    <XAxis dataKey="name" fontSize={9} tick={{ fill: isRec ? "#111315" : "#F7F7F4", opacity: 0.5 }} />
                                                    <Tooltip contentStyle={{ background: "#111315", border: "1px solid rgba(220,238,255,0.3)", fontSize: 11 }} />
                                                    <Bar dataKey="value">
                                                        {s.chart.map((_, idx) => <Cell key={idx} fill={STRATEGY_COLORS[idx % STRATEGY_COLORS.length]} />)}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <p className={`mt-4 text-xs leading-relaxed ${isRec ? "text-obsidian/70" : "text-arch-white/60"}`}>{s.rationale}</p>
                                    </div>
                                );
                            })}
                        </div>

                        {recommended && (
                            <div className="mt-8 p-6 md:p-8 border border-ice/40" style={{ background: "linear-gradient(135deg, rgba(220,238,255,0.06) 0%, rgba(91,127,176,0.15) 100%)" }} data-testid="di-recommendation">
                                <div className="chip chip-ai">Recommended Strategy</div>
                                <div className="mt-3 font-display italic text-3xl md:text-4xl">{recommended.label}</div>
                                <p className="mt-4 text-arch-white/80 leading-relaxed max-w-3xl">{result.recommended_narrative}</p>
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to={`/forgeos/early-access?address=${encodeURIComponent(result.query)}`} className="inline-flex items-center gap-3 bg-arch-white text-obsidian px-7 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ice" data-testid="di-cta-full">
                                See full analysis in ForgeOS <ArrowRight size={14} />
                            </Link>
                            <Link to={`/#forge-vision`} className="inline-flex items-center gap-3 border border-arch-white/40 text-arch-white px-7 py-4 text-[11px] uppercase tracking-[0.2em] hover:border-ice">
                                Compare to Forge Vision analysis
                            </Link>
                        </div>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
